import { useEffect, useState } from 'react';
import type { AppConfig, Theme } from '@avs/contract';
import { useApp } from '../state/AppContext.js';
import { showToast } from '../hooks/useToasts.js';
import { Icon } from '../icons/Icon.js';
import { Card } from '../components/Card.js';
import { Button } from '../components/Button.js';
import { Field } from '../components/Field.js';
import { ConfigHealth } from '../components/ConfigHealth.js';
import { NativeSelect } from '../components/Toolbar.js';

type SectionKey = 'import' | 'tos' | 'gen' | 'appear';

const SECTIONS: ReadonlyArray<{ key: SectionKey; label: string }> = [
  { key: 'import', label: '配置导入导出' },
  { key: 'tos',    label: 'TOS 存储' },
  { key: 'gen',    label: '默认生成参数 / 模型' },
  { key: 'appear', label: '外观与偏好' },
];

const RES_OPTIONS = [
  { value: '720p',  label: '720p' },
  { value: '1080p', label: '1080p' },
  { value: '2K',    label: '2K' },
] as const;
const DUR_OPTIONS = [
  { value: '5 秒',  label: '5 秒' },
  { value: '10 秒', label: '10 秒' },
  { value: '15 秒', label: '15 秒' },
] as const;

export function Settings() {
  const { state, actions } = useApp();
  const [sec, setSec] = useState<SectionKey>('tos');
  // 本地编辑缓冲：保存并应用之前不写 context
  const [draft, setDraft] = useState<AppConfig>(state.config);

  // state.config 变化时（例如其他视图改了）同步到 draft
  useEffect(() => { setDraft(state.config); }, [state.config]);

  function patch(p: Partial<AppConfig>) { setDraft({ ...draft, ...p }); }

  function save() {
    actions.updateConfig(draft);
    const tosOk = !!(draft.tos.bucket && draft.tos.accessKey && draft.tos.secretKey);
    showToast(tosOk ? '配置已保存并应用' : '已保存，TOS 仍待完善', tosOk ? 'ok' : 'info');
  }

  async function exportConfig() {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'avs-config.json';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast('已导出 avs-config.json', 'ok');
  }
  function importConfig() {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'application/json,.json';
    inp.onchange = () => {
      const f = inp.files?.[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const obj = JSON.parse(String(reader.result)) as Partial<AppConfig>;
          setDraft({
            tos:    { ...draft.tos,    ...(obj.tos ?? {}) },
            output: { ...draft.output, ...(obj.output ?? {}) },
            gen:    { ...draft.gen,    ...(obj.gen ?? {}) },
            theme:  obj.theme ?? draft.theme,
          });
          showToast('已导入，点击「保存并应用」生效', 'ok');
        } catch {
          showToast('配置文件解析失败', 'err');
        }
      };
      reader.readAsText(f);
    };
    inp.click();
  }

  const tosOk = !!(draft.tos.bucket && draft.tos.accessKey && draft.tos.secretKey);

  return (
    <>
      <Card>
        <div className="block-title">连接健康度</div>
        <div className="block-desc">这些配置会影响素材上传、任务提交与结果读取。密钥只保存在本机浏览器。</div>
        <div style={{ marginTop: 'var(--space-s)' }}>
          <ConfigHealth chips={[
            { ok: tosOk,             title: 'TOS 存储',  desc: tosOk ? '已配置 Bucket 与 AK/SK' : '缺少 Bucket 或 AK/SK' },
            { ok: !!draft.output.bucket, title: '输出位置', desc: draft.output.bucket ? '已配置生成结果写入位置' : '未配置生成结果写入位置' },
            { ok: true,              title: '素材目录',  desc: 'video/ · image/ · group/' },
            { ok: false,             title: '模型服务',  desc: '待对接（运维侧配置，前端不收集）' },
          ]} />
        </div>
      </Card>

      <div className="settings-grid">
        <div className="sub-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={'sub-nav-item' + (sec === s.key ? ' active' : '')}
              onClick={() => setSec(s.key)}
            >{s.label}</button>
          ))}
        </div>

        <div className="card settings-detail">
          {sec === 'import' && (
            <div className="fgroup">
              <div className="fgroup-title">配置导入导出</div>
              <div className="fgroup-desc">导入后会先覆盖当前页面中的设置；点击「保存并应用」后才会正式写入本地配置。</div>
              <div className="row">
                <Button leftIcon={<Icon name="upload" size={16} />} onClick={importConfig}>导入配置</Button>
                <Button leftIcon={<Icon name="download" size={16} />} onClick={() => void exportConfig()}>导出配置</Button>
              </div>
            </div>
          )}

          {sec === 'tos' && (
            <div className="fgroup">
              <div className="fgroup-title">TOS 存储 / 对象存储</div>
              <div className="fgroup-desc">用于上传本地视频和图片。兼容火山 TOS / 阿里 OSS / S3 协议。密钥仅保存在本机浏览器。</div>
              <Field label="Bucket 名称">
                <input className="input" placeholder="例如 avs-studio"
                  value={draft.tos.bucket}
                  onChange={(e) => patch({ tos: { ...draft.tos, bucket: e.target.value } })} />
              </Field>
              <Field label="对象路径前缀（选填）">
                <input className="input" placeholder="avs-studio/"
                  value={draft.tos.prefix}
                  onChange={(e) => patch({ tos: { ...draft.tos, prefix: e.target.value } })} />
              </Field>
              <div className="form-2">
                <Field label="Access Key">
                  <input className="input" type="password"
                    value={draft.tos.accessKey}
                    onChange={(e) => patch({ tos: { ...draft.tos, accessKey: e.target.value } })} />
                </Field>
                <Field label="Secret Key">
                  <input className="input" type="password"
                    value={draft.tos.secretKey}
                    onChange={(e) => patch({ tos: { ...draft.tos, secretKey: e.target.value } })} />
                </Field>
              </div>
              <div className="form-2">
                <Field label="Region">
                  <input className="input" placeholder="cn-beijing"
                    value={draft.tos.region}
                    onChange={(e) => patch({ tos: { ...draft.tos, region: e.target.value } })} />
                </Field>
                <Field label="Endpoint（选填）">
                  <input className="input" placeholder="tos-cn-beijing.volces.com"
                    value={draft.tos.endpoint}
                    onChange={(e) => patch({ tos: { ...draft.tos, endpoint: e.target.value } })} />
                </Field>
              </div>
            </div>
          )}

          {sec === 'gen' && (
            <>
              <div className="fgroup">
                <div className="fgroup-title">默认生成参数</div>
                <div className="fgroup-desc">新建任务时的默认值，可在任务表单里覆盖。</div>
                <div className="form-2">
                  <Field label="默认分辨率">
                    <NativeSelect<typeof draft.gen.resolution>
                      value={draft.gen.resolution}
                      onChange={(v) => patch({ gen: { ...draft.gen, resolution: v } })}
                      options={RES_OPTIONS}
                      width="100%"
                    />
                  </Field>
                  <Field label="默认时长">
                    <NativeSelect<typeof draft.gen.duration>
                      value={draft.gen.duration}
                      onChange={(v) => patch({ gen: { ...draft.gen, duration: v } })}
                      options={DUR_OPTIONS}
                      width="100%"
                    />
                  </Field>
                </div>
                <Field label="生成输出位置">
                  <input className="input" placeholder="tos://bucket/output/"
                    value={draft.output.bucket}
                    onChange={(e) => patch({ output: { ...draft.output, bucket: e.target.value } })} />
                </Field>
              </div>
              <div className="fgroup" style={{ marginTop: 'var(--space-l)' }}>
                <div className="fgroup-title">模型服务（运维侧）</div>
                <div className="fgroup-desc">视频替换 / 生成的底层模型由服务端配置，前端不收集模型 API Key。可选：火山方舟视觉模型、阿里通义万相、Runway、自建 ComfyUI 等。</div>
                <div className="alert alert-warn">
                  <Icon name="info" size={18} />
                  <div className="alert-body">
                    需要在服务端环境变量配置 <code>MODEL_PROVIDER</code> / <code>MODEL_API_KEY</code>，详见部署清单。
                  </div>
                </div>
              </div>
            </>
          )}

          {sec === 'appear' && (
            <div className="fgroup">
              <div className="fgroup-title">外观与偏好</div>
              <div className="fgroup-desc">主题与侧栏默认状态，立即生效并记忆。</div>
              <div className="setting-row">
                <div>
                  <div className="setting-name">主题</div>
                  <div className="setting-desc">跟随系统 / 亮色 / 暗色</div>
                </div>
                <div className="theme-pick">
                  {(['system','light','dark'] as Theme[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={'theme-btn' + (state.theme === t ? ' active' : '')}
                      onClick={() => actions.setTheme(t)}
                      title={t === 'system' ? '跟随系统' : t === 'light' ? '亮色' : '暗色'}
                    >
                      <Icon name={t === 'system' ? 'monitor' : t === 'light' ? 'sun' : 'moon'} size={16} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="setting-row">
                <div>
                  <div className="setting-name">侧栏默认</div>
                  <div className="setting-desc">下次打开是否展开侧栏</div>
                </div>
                <Button size="sm" onClick={actions.toggleCollapsed}>
                  {state.collapsed ? '当前：收起' : '当前：展开'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        variant="primary"
        leftIcon={<Icon name="check" size={16} />}
        onClick={save}
        className="save-fab"
      >保存并应用</Button>
    </>
  );
}
